import type { ClientEvent, SyncRequest } from '@chariot/contract';
import { db, getMeta, setMeta, type LocalEvent } from './db.ts';
import { deviceId } from './device.ts';
import { api, ApiError, NetworkError } from './api.ts';

/**
 * Outbox drain plus catalogue/log pull, in one round trip.
 *
 * Every send is idempotent (the server keys on the client-generated event id), so a reply
 * lost in transit costs nothing: the same batch is simply re-sent and the duplicates are
 * ignored. Nothing here ever blocks the UI.
 */

const MAX_BATCH = 500;
const MAX_PAGES = 50;

export type SyncOutcome =
  | { ok: true; pushed: number; pulled: number; rejected: number; reset: boolean }
  | { ok: false; reason: 'offline' | 'unauthenticated' | 'error'; detail?: string };

export async function pendingCount(): Promise<number> {
  return db.events.where('sync_state').equals('pending').count();
}

async function pendingBatch(): Promise<LocalEvent[]> {
  const rows = await db.events.where('sync_state').equals('pending').toArray();
  // Oldest first: a round_opened must land before the scans that reference it.
  rows.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : a.id < b.id ? -1 : 1));
  return rows.slice(0, MAX_BATCH);
}

export async function syncOnce(): Promise<SyncOutcome> {
  if (!navigator.onLine) return { ok: false, reason: 'offline' };

  let pushed = 0;
  let pulled = 0;
  let rejected = 0;
  let didReset = false;

  for (let page = 0; page < MAX_PAGES; page++) {
    const batch = await pendingBatch();
    const cursor = await getMeta<number>('cursor', 0);
    const req: SyncRequest = {
      device_id: await deviceId(),
      cursor,
      cursor_event_id: await getMeta<string | null>('cursor_event_id', null),
      events: batch.map((r) => r.event),
    };

    let res;
    try {
      res = await api.sync(req);
    } catch (e) {
      if (e instanceof NetworkError) return { ok: false, reason: 'offline' };
      if (e instanceof ApiError && e.unauthenticated) return { ok: false, reason: 'unauthenticated' };
      return { ok: false, reason: 'error', detail: e instanceof Error ? e.message : String(e) };
    }

    await db.transaction('rw', db.events, db.meta, db.sites, db.carts, db.rejections, async () => {
      if (res.reset) {
        // The server does not recognise our cursor, so everything we hold as *confirmed*
        // belongs to a history that no longer exists. Drop it and take the server's copy.
        //
        // Pending events are never touched: they are the operator's unsent work, and they
        // were pushed in this very request before the reset was signalled.
        await db.events.where('sync_state').equals('confirmed').delete();
        await db.sites.clear();
        await db.carts.clear();
        await setMeta('cursor', 0);
        await setMeta('cursor_event_id', null);
        didReset = true;
      }

      for (const id of res.accepted) {
        await db.events.update(id, { sync_state: 'confirmed' });
      }
      pushed += res.accepted.length;

      // Permanently unacceptable — drop rather than retry for ever, but keep the fact
      // visible: a scan refused as unknown_cart is a real operational event, not noise.
      for (const r of res.rejected) {
        const local = await db.events.get(r.id);
        await db.rejections.put({ ...r, seen_at: new Date().toISOString(), event: local?.event ?? null });
        await db.events.delete(r.id);
      }
      rejected += res.rejected.length;

      for (const e of res.events) {
        const { seq: _seq, received_at: _r, clock_skew_minutes: _s, ...rest } = e;
        const ev = rest as ClientEvent;
        await db.events.put({
          id: ev.id,
          at: ev.at,
          sync_state: 'confirmed',
          round_id: ev.round_id,
          cart_code: ev.type === 'cart_scanned' ? ev.cart_code : null,
          event: ev,
        });
      }
      pulled += res.events.length;

      // The catalogue arrives whole, so replacing it outright is correct and avoids any
      // delta-tracking bug. At a couple of hundred rows this is instant.
      await db.sites.clear();
      await db.sites.bulkPut(res.catalog.sites);
      await db.carts.clear();
      await db.carts.bulkPut(res.catalog.carts);

      await setMeta('cursor', res.cursor);
      // Remember which event sits at the cursor, so the next sync can prove the cursor is
      // still talking about the same history.
      const last = res.events.at(-1);
      if (last) await setMeta('cursor_event_id', last.id);
      await setMeta('stale_after_days', res.config.stale_after_days);
      await setMeta('last_sync', new Date().toISOString());
    });

    const moreToPush = (await pendingCount()) > 0 && res.accepted.length > 0;
    if (!res.has_more && !moreToPush) break;
  }

  return { ok: true, pushed, pulled, rejected, reset: didReset };
}
